import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Loader2, Wallet, Trash2, UserCog, TrendingUp, Clock, CheckCircle2,
} from "lucide-react";

const ESTADOS = [
  { value: "pendiente", label: "Pendiente", cls: "bg-[#F5EEE0] text-[#7A1E2E] border-[#C19B6C]/50" },
  { value: "parcial", label: "Parcial", cls: "bg-[#F5EEE0] text-[#3d2a2a] border-[#E5DFD3]" },
  { value: "pagado", label: "Pagado", cls: "bg-[#EEF6EE] text-[#1F5B2E] border-[#1F5B2E]/20" },
];
const METODOS = ["Transferencia", "Sinpe Móvil", "Efectivo", "Cheque", "Tarjeta", "Otro"];
const MONEDAS = ["CRC", "USD"];

const EMPTY = {
  concepto: "", proceso_id: null, monto: "", moneda: "CRC", fecha: "",
  estado: "pendiente", metodo_pago: "", factura: "", notas: "",
};

const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";
const labelCls = "text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold";

function fmt(monto, moneda) {
  const n = Number(monto || 0);
  const symbol = moneda === "USD" ? "$" : "₡";
  return `${symbol} ${new Intl.NumberFormat("es-CR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)}`;
}

function estadoBadge(estado) {
  const e = ESTADOS.find((x) => x.value === estado);
  return <Badge className={`${e?.cls || "bg-white"} font-normal border`}>{e?.label || estado}</Badge>;
}

function Kpi({ label, value, icon: Icon, tone }) {
  const tones = {
    total: "text-[#7A1E2E] bg-[#F5EEE0] border-[#C19B6C]/40",
    pagado: "text-[#1F5B2E] bg-[#EEF6EE] border-[#1F5B2E]/20",
    pendiente: "text-[#C25A5A] bg-[#FBEDEF] border-[#7A1E2E]/25",
  };
  return (
    <div className="rounded-xl bg-white border border-[#E5DFD3] p-5">
      <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${tones[tone]}`}>
        <Icon className="h-3 w-3" strokeWidth={2} />{label}
      </div>
      <div className="mt-3 font-display text-2xl text-[#1A0810]">{value}</div>
    </div>
  );
}

export default function CobrosTab({ clienteId }) {
  const { user } = useAuth();
  const canEdit = ["super_admin", "admin", "abogado"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [procesos, setProcesos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () =>
    api.get(`/clientes/${clienteId}/cobros`)
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Error cargando cobros"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get(`/clientes/${clienteId}/procesos`).then((r) => setProcesos(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [clienteId]);

  const totals = useMemo(() => {
    const acc = { total: 0, pagado: 0, pendiente: 0 };
    items.forEach((c) => {
      const m = Number(c.monto || 0);
      acc.total += m;
      if (c.estado === "pagado") acc.pagado += m;
      else if (c.estado === "pendiente") acc.pendiente += m;
      else if (c.estado === "parcial") acc.pendiente += m / 2; // aprox
    });
    return acc;
  }, [items]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c, monto: String(c.monto ?? "") }); setDialogOpen(true); };

  const save = async () => {
    if (!form.concepto.trim()) { toast.error("El concepto es obligatorio"); return; }
    const monto = parseFloat(form.monto);
    if (isNaN(monto) || monto < 0) { toast.error("Monto inválido"); return; }
    setSaving(true);
    try {
      const payload = { ...form, monto, proceso_id: form.proceso_id || null };
      if (editing) {
        await api.put(`/cobros/${editing.id}`, payload);
        toast.success("Cobro actualizado");
      } else {
        await api.post(`/clientes/${clienteId}/cobros`, payload);
        toast.success("Cobro registrado");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/cobros/${deleteTarget.id}`);
      toast.success("Cobro eliminado");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar");
    }
  };

  const procesoTitulo = (procesoId) => {
    if (!procesoId) return "—";
    const p = procesos.find((x) => x.id === procesoId);
    return p?.titulo || "—";
  };

  return (
    <div className="space-y-6" data-testid="cobros-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kpi label="Total facturado" value={fmt(totals.total, "CRC")} icon={TrendingUp} tone="total" />
        <Kpi label="Cobrado" value={fmt(totals.pagado, "CRC")} icon={CheckCircle2} tone="pagado" />
        <Kpi label="Por cobrar" value={fmt(totals.pendiente, "CRC")} icon={Clock} tone="pendiente" />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#C25A5A] font-semibold">Cobros del cliente</div>
          <h3 className="mt-1 font-display text-xl text-[#1A0810]">Movimientos ({items.length})</h3>
        </div>
        {canEdit && (
          <Button onClick={openNew} data-testid="cobros-add-button" className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
            <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> Registrar cobro
          </Button>
        )}
      </div>

      <div className="rounded-xl bg-white border border-[#E5DFD3] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#7A1E2E] mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <Wallet className="h-10 w-10 text-[#C19B6C] mx-auto" strokeWidth={1.3} />
            <p className="mt-3 text-sm text-[#6B5D57]">Aún no se han registrado cobros para este cliente.</p>
            {canEdit && (
              <Button onClick={openNew} className="mt-6 bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
                <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> Registrar primer cobro
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F5EEE0] hover:bg-[#F5EEE0] border-b border-[#E5DFD3]">
                <TableHead className="text-[#3d2a2a] font-semibold">Concepto</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Proceso</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Fecha</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold text-right">Monto</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Estado</TableHead>
                {canEdit && <TableHead className="text-[#3d2a2a] font-semibold text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`cobro-row-${c.id}`} className="border-b border-[#E5DFD3] hover:bg-[#FBF9F4]">
                  <TableCell>
                    <div className="text-sm font-medium text-[#1A0810]">{c.concepto}</div>
                    {c.factura && <div className="text-xs text-[#6B5D57] mt-0.5">Fact. {c.factura}</div>}
                    {c.metodo_pago && <div className="text-[11px] text-[#6B5D57] mt-0.5">{c.metodo_pago}</div>}
                  </TableCell>
                  <TableCell><span className="text-sm text-[#3d2a2a]">{procesoTitulo(c.proceso_id)}</span></TableCell>
                  <TableCell><span className="text-sm text-[#3d2a2a]">{c.fecha || "—"}</span></TableCell>
                  <TableCell className="text-right">
                    <span className="font-display text-base text-[#7A1E2E]">{fmt(c.monto, c.moneda)}</span>
                  </TableCell>
                  <TableCell>{estadoBadge(c.estado)}</TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`cobro-edit-${c.id}`} className="h-8 w-8 hover:bg-[#F5EEE0]">
                          <UserCog className="h-4 w-4 text-[#3d2a2a]" strokeWidth={1.8} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(c)} data-testid={`cobro-delete-${c.id}`} className="h-8 w-8 hover:bg-[#FBEDEF]">
                          <Trash2 className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8} />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FBF9F4] border-[#E5DFD3]" data-testid="cobro-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A0810]">
              {editing ? "Editar cobro" : "Registrar nuevo cobro"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className={labelCls}>Concepto *</Label>
              <Input data-testid="cobro-form-concepto" value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} className={`${inputCls} mt-2`} placeholder="Honorarios por audiencia preliminar" />
            </div>

            <div>
              <Label className={labelCls}>Proceso relacionado</Label>
              <Select value={form.proceso_id || "none"} onValueChange={(v) => setForm({ ...form, proceso_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="cobro-form-proceso" className={`${inputCls} mt-2`}>
                  <SelectValue placeholder="Sin proceso asociado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin proceso asociado</SelectItem>
                  {procesos.map((p) => <SelectItem key={p.id} value={p.id}>{p.titulo}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className={labelCls}>Monto *</Label>
                <Input
                  data-testid="cobro-form-monto"
                  type="number" min="0" step="0.01"
                  value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })}
                  className={`${inputCls} mt-2`} placeholder="850000"
                />
              </div>
              <div>
                <Label className={labelCls}>Moneda</Label>
                <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                  <SelectTrigger data-testid="cobro-form-moneda" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONEDAS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={labelCls}>Fecha</Label>
                <Input data-testid="cobro-form-fecha" type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={`${inputCls} mt-2`} />
              </div>
              <div>
                <Label className={labelCls}>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                  <SelectTrigger data-testid="cobro-form-estado" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ESTADOS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Método de pago</Label>
                <Select value={form.metodo_pago || "none"} onValueChange={(v) => setForm({ ...form, metodo_pago: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="cobro-form-metodo" className={`${inputCls} mt-2`}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Ninguno —</SelectItem>
                    {METODOS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Nº de factura</Label>
                <Input data-testid="cobro-form-factura" value={form.factura} onChange={(e) => setForm({ ...form, factura: e.target.value })} className={`${inputCls} mt-2`} placeholder="2026-014" />
              </div>
            </div>

            <div>
              <Label className={labelCls}>Notas</Label>
              <Textarea data-testid="cobro-form-notas" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="mt-2 min-h-[64px] bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]" placeholder="Observaciones sobre el cobro" />
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="cobro-save-button" className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar cambios" : "Registrar cobro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#FBF9F4] border-[#E5DFD3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A0810]">Eliminar cobro</AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a3838]">
              ¿Está seguro de eliminar el cobro <b>{deleteTarget?.concepto}</b>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="cobro-confirm-delete" className="bg-[#7A1E2E] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
